<template>
  <v-app id="app" :style="{ background: 'var(' + appBackground + ')' }">
    <v-app-bar ref="appBarEl"
      :style="{background: toolbarGradient}"
      app
      dark
      flat
      :clipped-left="!rtl"
      :clipped-right="rtl"
      v-show="toolbar"
    >
      <v-app-bar-nav-icon
        id="nav-drawer-toggle"
        dark
        @click.stop="drawer = !drawer"
      >
      </v-app-bar-nav-icon>

      <v-spacer v-if="!isScreenLarge"></v-spacer>

      <v-toolbar-title>
        <v-img
          src="img/logo/beige_logo.png"
          max-width="130px"
          contain
        >
        </v-img>
      </v-toolbar-title>

      <v-spacer></v-spacer>
      <v-btn id="search-conditions" @click="showSearchModal=true" icon
        class="search-conditions-btn"
        title="Search conditions covered by ThinkMD"
      >
        <v-icon>{{ icons.mdiStethoscope }}</v-icon>
      </v-btn>

      <v-btn id="change-language" @click.stop="drawer = !drawer" icon>
        <v-icon>{{ icons.mdiTranslate }}</v-icon>
      </v-btn>

      <v-btn id="pause" v-if="encounterIsActive && showPauseButton" @click="showPauseModal=true" icon>
        <v-icon>{{ icons.mdiPause }}</v-icon>
      </v-btn>

      <v-btn id="information" v-if="false" @click="info = !info" icon>
        <v-icon>{{ icons.mdiInformationVariant }}</v-icon>
      </v-btn>
    </v-app-bar>

    <scroll-arrow
      id="app-scroll-arrow"
      :show="hasOverflow"
      :style="scrollArrowStyle"
      position="fixed"
      :arrowAlignment="arrowAlignment"
      color="rgba(178, 194, 198)"
      v-on:scrollDown="scrollDown"
      v-on:scrollUp="scrollUp"
    ></scroll-arrow>

    <modal
      tokenType="pause"
      coverBackground
      :show="showPauseModal"
      @cancel="showPauseModal=false"
      @agree="saveEncounterAndQuit"
    ></modal>
    <search-modal 
      :show="showSearchModal" 
      @close="showSearchModal=false"
      @open-suggestion-form="showSuggestionForm=true"
    ></search-modal>

    <user-suggestion-form v-model="showSuggestionForm" />

    <v-content ref="contentEl" style="overflow:auto;">
      <v-img v-if="isOnStart" contain class="background-img" src="img/landing/particle_man.webp" ></v-img>
      <v-container id="top-container" class="top-responsive d-flex flex-column align-content-start" ref="topEl">
        <app-status ref="appStatusEl"></app-status>
        <assessment-image
          @showVolumeControl="setShowVolumeControl"
          :muteVideo="muteVideo"
          ref="assessmentImageEl">
        </assessment-image>
      </v-container>
      <v-container
        id="content-container"
        :style="containerStyle"
        :class="containerClass"
        >
        <router-view
          :maxPanelHeight="this.maxHeight(panelOffset)"
          :topPanelOffset="topOffset"
          :navPadding="navTotalOffset"
          :style="routerStyle"
          :showVolumeControl="showVolumeControl"
          @videoMuted="setMuteVideo"
          >
        </router-view>
        <bottom v-show="showNav" ref="bottomEl" class="nav-responsive" ></bottom>
      </v-container>
    </v-content>

    <v-overlay
      opacity="1"
      :value="!dialog"
      eager
      color="primaryblue"
      class="overlay"
    >
      <v-card
        class="loading-dialog intro-dark"
      >
        <v-card-title class="justify-center pa-0">
          <mrx-translation class="pt-4" :token="`app.loading.title`"></mrx-translation>
        </v-card-title>
        <v-card-text class="pa-0">
          <v-row class="mx-4 py-4" no-gutters align="center" justify="center">
            <v-progress-linear
              indeterminate
              rounded
              color="lightblue"
              class="my-2"
              style="width: 60%"
            ></v-progress-linear>
          </v-row>
        </v-card-text>
      </v-card>
    </v-overlay>

    <v-navigation-drawer
      v-model="drawer"
      :disable-route-watcher="true"
      :clipped="isScreenLarge"
      app
      :right="rtl"
      :style="{height: this.availableScreenHeight + 'px'}"
      >
      <drawer v-on:navToggle="drawer = !drawer"></drawer>
    </v-navigation-drawer>

  </v-app>

</template>
<script>
  import drawer from '@/components/layouts/Drawer'
  import bottom from '@/components/layouts/Bottom'
  import AppStatus from '@/components/layouts/AppStatus'
  import Modal from '@/components/layouts/Modal'
  import AssessmentImage from '@/components/layouts/AssessmentImage'
  import ScrollArrow from '@/components/layouts/ScrollArrow.vue'
  import searchModal from '@/components/layouts/SearchModal.vue'
  import UserSuggestionForm from '@/components/layouts/UserSuggestionForm.vue'
  import icons from '@/mixins/icons'
  import ui from '@/mixins/ui'
  import scrollArrow from '@/mixins/scrollArrow'
  import { mapGetters } from 'vuex'

  let capitalize = (value) => {
    if (!value) return ''
    value = value.toString()
    return value.charAt(0).toUpperCase() + value.slice(1)
  }

  export default {
    components: { drawer, bottom, ScrollArrow, AppStatus, AssessmentImage, Modal, searchModal, UserSuggestionForm },
    mixins: [ icons, ui, scrollArrow ],
    name: 'App',
    data() {
      return {
        drawer: false,
        dialog: false,
        topHeight: 0,
        topOffset: 0,
        toolbarHeight: 0,
        appStatusHeight: 0,
        availableScreenHeight: 0,
        navHeight: 0,
        navBottomOffset: 0,
        assessmentImageObserver: null,
        toolbarObserver: null,
        navObserver: null,
        contentObserver: null,
        showVolumeControl: false,
        muteVideo: true,
        showPauseModal: false,
        showSearchModal: false,
        showSuggestionForm: false,
      }
    },

    computed: {
      rtl() { return this.$store.state.v4.language.rtl; },
      lang() { return this.$store.state.v4.language.lang; },
      isScreenLarge() {
        return (this.$vuetify.breakpoint.lgAndUp);
      },
      info: {
        get() {
          return this.$store.state.v4.navigate.showInfo;
        },
        set(val) {
          this.$store.commit('v4/navigate/set', { showInfo: val });
        }
      },
      toolbar() {
        return (this.$route.meta.top === undefined || this.$store.getters['v4/mode/get']('show')) ? true : false;
      },
      showNav() {
        return this.$route.meta.show !== false;
      },
      centeredRoute() {
        return this.$route.meta.position === 'center';
      },
      panelOffset() {
        let panelLimit = 0;
        let progressMargin = this.navBottomOffset*2;

        panelLimit = this.toolbarHeight + this.appStatusHeight;

        // nav bar overlaps non-centered routes so its height only matters for centered ones
        if (this.centeredRoute && this.showNav) {
          panelLimit = panelLimit + this.navHeight + (progressMargin);
        }

        return panelLimit;
      },
      navTotalOffset() {
        return (this.showNav) ? this.navHeight + (this.navBottomOffset*2) : 0
      },
      navOffsetFromBottom() {
        return this.navHeight + (this.navBottomOffset/2)
      },
      routerStyle() {
        if (this.centeredRoute) {
          return {
            marginTop: this.topHeight + 'px',
          }
        }
        else return {}
      },
      containerStyle() {
        const bottomPadding = this.centeredRoute && this.showNav ? "96px !important" : "0px"

        return {
          height: "100%",
          paddingBottom: bottomPadding
        }
      },
      containerClass() {
        return "d-flex flex-wrap panel-responsive pa-0 " + (this.centeredRoute ? "align-center centered-panel-responsive" : "align-end")
      },
      scrollArrowStyle() {
        return {
          height: "100vh",
          minWidth: "100%",
          paddingTop: this.toolbarHeight + 'px',
          paddingLeft: this.drawer && this.isScreenLarge ? '256px' : '0px'
        }
      },
      theme(){
        return (this.$vuetify.theme.dark) ? 'dark' : 'light'
      },
      appBackground() {
        let tone = this.settings('skinTone')

        if (tone === 'black') {
          return "--black-tone-background"
        } else {
          return "--primary-blue"
        }
      },
      toolbarGradient() {
        let tone = this.settings('skinTone')

        if (tone === 'black') {
          return 'linear-gradient(180deg, RGBA(85, 156, 180, 1) 0%, 30%, rgba(85, 156, 180, 0) 100%)'
        } else {
          return 'linear-gradient(180deg, RGBA(6, 25, 30, 1) 0%, rgba(6, 25, 30, 0) 100%)'
        }
      },
      isOnStart() {
        return (this.$route.name === 'Start')
      },
      showPauseButton() {
        return this.$store.state.settings.engagements_features.encounterPauseResume
      },
      currentUserId() {
        try {
          const prefGetter = this.$store.getters['v4/preference/getResponse'];
          let userId = prefGetter ? prefGetter('user_id', 'number')?.value : null;
          if (!userId) {
            const panelGetter = this.$store.getters['v4/panel/getResponse'];
            userId = panelGetter ? panelGetter('user_id', 'number')?.value : null;
          }
          return userId || null;
        } catch (e) {
          return null;
        }
      },
      ...mapGetters(
        {
          mode: 'v4/mode/get',
          settings: 'v4/settings/get',
          encounterIsActive: 'v4/track/active',
        }
      )
    },

    methods: {
      async init() {
        this.dialog = await this.$store.dispatch('v4/init')
        
        if (this.currentUserId) {
          this.identifyCurrentUser(this.currentUserId, 'session_resume')
        }
      },
      identifyCurrentUser(userId, loginMethod = 'panel_input') {
        try {
          const deviceId = this.$getDeviceId()
          const userType = this.$store.getters['v4/track/settings']('audience') === 'chw' ? 'professional' : 'consumer'
          const registrationId = this.$store.state.v4.registration.registrationId
          const hrId = this.$store.state.v4.registration.hrId
          
          const previousUserId = localStorage.getItem('last_user_id')
          if (previousUserId && previousUserId !== userId.toString()) {
            this.$track(this.$analyticsEvents.USER_ID_CHANGED, {
              previous_user_id: previousUserId,
              new_user_id: userId,
              device_id: deviceId
            })
          }
          
          this.$track(this.$analyticsEvents.USER_IDENTIFIED, {
            user_id: userId,
            device_id: deviceId,
            user_type: userType,
            login_method: loginMethod
          })
          
          this.$identifyUser(userId, {
            user_type: userType,
            registration_id: registrationId,
            hr_id: hrId,
            device_id: deviceId
          })
          
          localStorage.setItem('last_user_id', userId.toString())
        } catch (error) {
          // Silently fail if user tracking fails
        }
      },
      saveEncounterAndQuit() {
        this.$store.dispatch('v4/encounters/save')
        this.showPauseModal = false
        this.$track(this.$analyticsEvents.ENCOUNTER_PAUSE)
        this.$router.push({ path: '/preference/summary' })
      },
      setShowVolumeControl(event) {
        this.showVolumeControl = event;
      },
      setMuteVideo(event) {
        this.muteVideo = event;
      },
      onWindowScroll(e) {
        // readjusts available screen height for mobile when url bar disappears
        this.availableScreenHeight = window.innerHeight
        this.onScroll(e)
      },
      onWindowResize() {
        this.availableScreenHeight = window.innerHeight
        this.onContentResize()
      },
      onContentResize() {
        this.onScrollContentResize(this.navOffsetFromBottom)
      },
      onImageResize() {
        let assessmentImageHeight = 0;
        if (this.$refs.assessmentImageEl) {
          this.topOffset = this.$refs.assessmentImageEl.$el.getBoundingClientRect().bottom;
          assessmentImageHeight = this.$refs.assessmentImageEl.$el.clientHeight;
        }

        if (this.$refs.appStatusEl) {
          this.appStatusHeight = this.$refs.appStatusEl.$el.clientHeight;
        }

        this.topHeight = this.appStatusHeight + assessmentImageHeight;
      },
      onToolbarResize() {
        if (this.$refs.appBarEl) {
          this.toolbarHeight = this.$refs.appBarEl.$el.offsetHeight
        }
      },
      onNavResize() {
        let navEl = this.$refs.bottomEl
        if (navEl) {
          this.navHeight = navEl.$el.offsetHeight
          this.navBottomOffset = 40
        }
      },
      scrollUp() {
        window.scrollTo(0, 0)
      },
      scrollDown() {
        const contentEl = this.$refs.contentEl.$el
        window.scrollTo(0, contentEl.scrollHeight)
      }
    },
    mounted() {
      window.addEventListener('scroll', this.onWindowScroll)
      window.addEventListener('resize', this.onWindowResize)

      this.assessmentImageObserver = new ResizeObserver(this.onImageResize)
      this.toolbarObserver = new ResizeObserver(this.onToolbarResize)
      this.navObserver = new ResizeObserver(this.onNavResize)
      this.contentObserver = new ResizeObserver(this.onContentResize)

      if (this.$refs.assessmentImageEl?.$el) {
        this.assessmentImageObserver.observe(this.$refs.assessmentImageEl.$el)
      }
      if (this.$refs.appBarEl?.$el) {
        this.toolbarObserver.observe(this.$refs.appBarEl.$el)
      }
      if (this.$refs.bottomEl?.$el) {
        this.navObserver.observe(this.$refs.bottomEl.$el)
        this.observeForIntersection(this.$refs.bottomEl.$el)
      }
      if (this.$refs.contentEl?.$el) {
        this.observedElement = this.$refs.contentEl.$el
        this.contentObserver.observe(this.$refs.contentEl.$el)
      }

      try {
        this.$router.onReady(() => {
          this.init()
        })
      }
      catch (e) { console.log(e) }
    },
    beforeDestroy() {
      window.removeEventListener('scroll', this.onWindowScroll)
      window.removeEventListener('resize', this.onWindowResize)
    },
    watch: {
      currentUserId(newUserId, oldUserId) {
        if (newUserId && newUserId !== oldUserId) {
          this.identifyCurrentUser(newUserId, 'panel_input')
        }
      },
      '$route': {
        handler: function(route) {

          if (this.$route.query) {

            if (this.$route.query.lang_filters === 'clear') {
              this.$store.commit('v4/language/set', { filters: [] })
            }
            else if (this.$route.query.lang_filters) {
              this.$store.commit('v4/language/set', { filters: this.$route.query.lang_filters.split(',') })
            }

            if (this.$route.query.fhirBundle) {
              this.$store.dispatch('v4/observation/parse', this.$route.query.fhirBundle)
            }

            Object.entries(this.$route.query).forEach(param => {
              let [name, value] = param
              if (/^tmd_/.test(name)) this.$store.commit('v4/settings/params', {
                [name.split('_')[1]]: value
              })
            })
          }

          if (route.meta.title) {
            this.$headful({ title: route.meta.title })
          }
          else if (route.name === 'Panel') {
            this.$headful({ title: route.params.panels_name.split('_').map(capitalize).join(' ') })
          }
          else {
            this.$headful({ title: route.name })
          }
        },
        deep: true,
        immediate: true
      }
    }
  }
</script>

<style scoped>
  .search-conditions-btn {
    background-color: #FFC107;
    margin-right: 8px;
  }
  .search-conditions-btn:hover {
    background-color: #FFD54F;
  }

  .centered-panel-responsive {
    width: 100%;
  }

  .background-img {
    max-height: 100%;
    max-width: 100%;
    position: absolute;
    top: 0;
    left: 50%;
    transform: translate(-50%, 0);
  }

  .loading-dialog {
    width: 100%;
    min-height: fit-content;
  }

  .overlay {
    align-items: end;
    padding: 40px;
  }

  .overlay >>> .v-overlay__content {
    width: 100%;
    min-height: fit-content;
  }

  .top-responsive {
    height: 100%;
    max-height: 50vh;
    max-width: 100vw;
    min-height: 0px;
    position: absolute;
    left: 50%;
    transform: translate(-50%, 0);
    padding: 0px;
  }

  @media screen and (min-width: 750px) {
    .panel-responsive {
      min-width: 400px;
      max-width: 750px;
    }

    .nav-responsive {
      max-width: 675px;
    }

    .centered-panel-responsive {
      width: fit-content;
    }

    .top-responsive {
      max-width: 750px;
    }

    .overlay >>> .v-overlay__content {
      max-width: 670px;
    }
  }

</style>
